import { Cell, CrystalKind, Enemy, GameState, LETTERS, Mover } from "./types";
import { lerpPos } from "./engine";
import { inBounds } from "./level";
import { Sprites } from "./sprites";
import { t } from "./i18n";

const FRAME_MS = 110;
export const PUP_HOP = true;
const LETTER_COLORS = ["#f472b6", "#facc15", "#4ade80", "#38bdf8", "#c084fc"];

export const themeFor = (level: number): 1 | 2 | 3 | 4 | 5 | 6 => (([1, 2, 3, 4, 5, 6] as const)[Math.floor((level - 1) / 3) % 6]);

function drawFrame(ctx: CanvasRenderingContext2D, img: HTMLImageElement, frame: number, x: number, y: number, w: number, h: number) {
  const frames = Math.max(1, Math.round(img.width / img.height));
  const fw = img.width / frames;
  ctx.drawImage(img, (frame % frames) * fw, 0, fw, img.height, x, y, w, h);
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawBall(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, base: string, dark: string, alpha = 1) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = "rgba(0,0,0,0.28)";
  ctx.beginPath();
  ctx.ellipse(cx + r * 0.12, cy + r * 0.45, r * 0.95, r * 0.55, 0, 0, Math.PI * 2);
  ctx.fill();
  const g = ctx.createRadialGradient(cx - r * 0.35, cy - r * 0.4, r * 0.1, cx, cy, r);
  g.addColorStop(0, "#ffffff");
  g.addColorStop(0.35, base);
  g.addColorStop(1, dark);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.beginPath();
  ctx.ellipse(cx - r * 0.38, cy - r * 0.42, r * 0.22, r * 0.14, -0.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawBone(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, glow: boolean, now: number) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(-Math.PI / 5);
  if (glow) { ctx.shadowColor = "#fbbf24"; ctx.shadowBlur = 12 + Math.sin(now / 120) * 6; }
  const g = ctx.createLinearGradient(0, -r * 0.5, 0, r * 0.5);
  g.addColorStop(0, "#fff7d6");
  g.addColorStop(0.55, "#fbbf24");
  g.addColorStop(1, "#b45309");
  ctx.fillStyle = g;
  ctx.strokeStyle = "#7c2d12";
  ctx.lineWidth = Math.max(1, r * 0.12);
  const L = r * 0.75, k = r * 0.3;
  ctx.beginPath();
  ctx.moveTo(-L, -k * 0.6);
  ctx.lineTo(L, -k * 0.6);
  ctx.arc(L, -k * 0.55, k, Math.PI * 1.5, Math.PI * 0.5);
  ctx.arc(L, k * 0.55, k, Math.PI * 1.5, Math.PI * 0.5);
  ctx.lineTo(-L, k * 0.6);
  ctx.arc(-L, k * 0.55, k, Math.PI * 0.5, Math.PI * 1.5);
  ctx.arc(-L, -k * 0.55, k, Math.PI * 0.5, Math.PI * 1.5);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

function drawCrystal(ctx: CanvasRenderingContext2D, s: GameState, sprites: Sprites, kind: CrystalKind, px: number, py: number, cell: number, now: number, alpha = 1) {
  const cx = px + cell / 2, cy = py + cell / 2;
  if (kind === "diamond" || kind === "diamond2") {
    const img = sprites.bone_ball;
    const stacked = kind === "diamond2";
    const glow = s.power > 0 || stacked;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = "rgba(0,0,0,0.3)";
    ctx.beginPath(); ctx.ellipse(cx, cy + cell * 0.36, cell * 0.36, cell * 0.12, 0, 0, Math.PI * 2); ctx.fill();
    if (glow) { ctx.shadowColor = "#fbbf24"; ctx.shadowBlur = 14 + Math.sin(now / 120) * 6; }
    const sz = cell * (stacked ? 1.04 : 0.92);
    if (img) ctx.drawImage(img, cx - sz / 2, cy - sz / 2, sz, sz);
    else { drawBall(ctx, cx, cy, cell * 0.44, "#fde68a", "#b45309", 1); drawBone(ctx, cx, cy, cell * 0.3, glow, now); }
    if (stacked) {
      ctx.shadowBlur = 0;
      ctx.fillStyle = "#7c2d12";
      ctx.beginPath(); ctx.arc(cx + cell * 0.3, cy - cell * 0.3, cell * 0.17, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#fff7ed";
      ctx.font = `700 ${cell * 0.26}px Fredoka, sans-serif`;
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText("2", cx + cell * 0.3, cy - cell * 0.29);
    }
    ctx.restore();
    return;
  }
  if (kind === "bounce1" || kind === "bounce2") {
    const two = kind === "bounce2";
    drawBall(ctx, cx, cy, cell * 0.44, two ? "#f5d0fe" : "#d1fae5", two ? "#9333ea" : "#059669", alpha);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = Math.max(1.5, cell * 0.07);
    ctx.lineCap = "round";
    const n = two ? 2 : 1;
    for (let i = 0; i < n; i++) {
      const ox = cx + (i - (n - 1) / 2) * cell * 0.22;
      ctx.beginPath();
      ctx.moveTo(ox - cell * 0.1, cy - cell * 0.02);
      ctx.lineTo(ox, cy - cell * 0.14);
      ctx.lineTo(ox + cell * 0.1, cy - cell * 0.02);
      ctx.moveTo(ox - cell * 0.1, cy + 0.14 * cell);
      ctx.lineTo(ox, cy + cell * 0.02);
      ctx.lineTo(ox + cell * 0.1, cy + 0.14 * cell);
      ctx.stroke();
    }
    ctx.restore();
    return;
  }
  if (kind === "bomb") {
    const armed = s.bombs.find((b) => Math.abs((b.x + 0.5) * cell - cx) < 1 && Math.abs((b.y + 0.5) * cell - cy) < 1);
    const pulse = armed ? 0.5 + 0.5 * Math.sin(now / (40 + armed.timer / 8)) : 0;
    drawBall(ctx, cx, cy, cell * (0.42 + pulse * 0.05), pulse > 0.5 ? "#fca5a5" : "#64748b", pulse > 0.5 ? "#7f1d1d" : "#0f172a", alpha);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = "#d6d3d1";
    ctx.lineWidth = Math.max(1.5, cell * 0.06);
    ctx.beginPath();
    ctx.moveTo(cx + cell * 0.12, cy - cell * 0.3);
    ctx.quadraticCurveTo(cx + cell * 0.3, cy - cell * 0.5, cx + cell * 0.36, cy - cell * 0.36);
    ctx.stroke();
    const spark = 0.5 + Math.sin(now / 60) * 0.5;
    ctx.fillStyle = "#fde047";
    ctx.shadowColor = "#f97316";
    ctx.shadowBlur = 10 + spark * 8;
    ctx.beginPath(); ctx.arc(cx + cell * 0.36, cy - cell * 0.36, cell * (0.06 + spark * 0.05), 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = "#f8fafc";
    ctx.font = `700 ${cell * 0.34}px Fredoka, sans-serif`;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText("!", cx, cy + cell * 0.04);
    ctx.restore();
    return;
  }
  if (kind === "ice") {
    drawBall(ctx, cx, cy, cell * 0.44, "#e0f2fe", "#0284c7", alpha);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = Math.max(1.5, cell * 0.06);
    ctx.lineCap = "round";
    for (let i = 0; i < 3; i++) {
      const a = (i * Math.PI) / 3 + now / 1500;
      ctx.beginPath();
      ctx.moveTo(cx - Math.cos(a) * cell * 0.22, cy - Math.sin(a) * cell * 0.22);
      ctx.lineTo(cx + Math.cos(a) * cell * 0.22, cy + Math.sin(a) * cell * 0.22);
      ctx.stroke();
    }
    ctx.restore();
    return;
  }
  drawBall(ctx, cx, cy, cell * 0.44, "#f1f5f9", "#94a3b8", alpha);
}

function drawSprite(ctx: CanvasRenderingContext2D, img: HTMLImageElement | undefined, cx: number, cy: number, size: number, flip: boolean, frame: number, fallback: () => void) {
  ctx.save();
  ctx.translate(cx, cy);
  if (flip) ctx.scale(-1, 1);
  if (img) drawFrame(ctx, img, frame, -size / 2, -size / 2, size, size);
  else fallback();
  ctx.restore();
}

function drawEnemy(ctx: CanvasRenderingContext2D, sprites: Sprites, e: Enemy, cell: number, now: number, frozen: boolean, edible: boolean) {
  const p = lerpPos(e);
  const cx0 = (p.x + 0.5) * cell, cy0 = (p.y + 0.5) * cell;
  if (e.kind === "bouncer") {
    const squash = frozen ? 0 : Math.abs(Math.sin(now / 90)) * 0.08;
    ctx.save();
    ctx.translate(cx0, cy0);
    ctx.scale(1 + squash, 1 - squash);
    drawBall(ctx, 0, 0, cell * 0.4, "#3b82f6", "#1e3a8a");
    ctx.restore();
    return;
  }
  const isBat = e.kind === "bat";
  const fly = isBat && e.flying;
  const bob = frozen ? 0 : fly ? Math.sin(now / 90 + e.id) * cell * 0.08 : Math.sin(now / 130 + e.id) * cell * 0.03;
  const cx = cx0;
  const cy = cy0 + bob - (fly ? cell * 0.12 : 0);
  const img = isBat && !fly ? sprites.bat_rest : sprites[e.kind];
  const frame = frozen || (isBat && !fly) ? 0 : Math.floor(now / (fly ? 70 : FRAME_MS) + e.id * 7) % 4;
  const flip = e.kind === "scorpion" ? e.dir === "right" : e.dir === "left";
  if (fly) {
    ctx.fillStyle = "rgba(0,0,0,0.3)";
    ctx.beginPath();
    ctx.ellipse(cx, cy0 + cell * 0.3, cell * 0.28, cell * 0.12, 0, 0, Math.PI * 2);
    ctx.fill();
  } else {
    const halo = ctx.createRadialGradient(cx, cy0, cell * 0.1, cx, cy0, cell * 0.5);
    halo.addColorStop(0, "rgba(0,0,0,0.42)");
    halo.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(cx, cy0, cell * 0.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.save();
  if (edible) ctx.globalAlpha = 0.55 + Math.sin(now / 80) * 0.25;
  drawSprite(ctx, img, cx, cy, cell * (fly ? 1.15 : isBat ? 0.9 : 1.02), flip, frame, () => {
    ctx.fillStyle = e.kind === "snake" ? "#22C55E" : isBat ? "#7e22ce" : e.kind === "spider" ? "#c4b5fd" : "#DC2626";
    ctx.beginPath();
    ctx.arc(0, 0, cell * 0.36, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.restore();
  if (frozen) {
    ctx.save();
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = "#93c5fd";
    ctx.shadowColor = "#bae6fd";
    ctx.shadowBlur = 12;
    roundRect(ctx, cx - cell * 0.42, cy - cell * 0.42, cell * 0.84, cell * 0.84, cell * 0.2);
    ctx.fill();
    ctx.restore();
  }
}

function drawPlayer(ctx: CanvasRenderingContext2D, sprites: Sprites, s: GameState, cell: number, now: number) {
  const p = s.player;
  const pos = lerpPos(p);
  const cx = (pos.x + 0.5) * cell;
  const cy = (pos.y + 0.5) * cell;
  ctx.save();
  if (p.deadTimer > 0) {
    const k = 1 - p.deadTimer / 1100;
    ctx.globalAlpha = Math.max(0, 1 - k);
    ctx.translate(cx, cy);
    ctx.rotate(k * Math.PI * 2);
    ctx.translate(-cx, -cy);
  } else if (s.power > 0) {
    ctx.shadowColor = "#22c55e";
    ctx.shadowBlur = 16 + Math.sin(now / 100) * 8;
  } else if (s.ghost > 0) {
    ctx.globalAlpha = 0.5 + Math.sin(now / 120) * 0.15;
    ctx.shadowColor = "#fde68a";
    ctx.shadowBlur = 18;
  } else if (p.invuln > 0 && Math.floor(now / 110) % 2 === 0) {
    ctx.globalAlpha = 0.45;
  }
  const moving = p.t < 1;
  const hopK = moving ? Math.sin(p.t * Math.PI) : 0;
  const walk = PUP_HOP ? -hopK * cell * 0.05 : moving ? Math.sin(p.t * Math.PI * 2) * cell * 0.04 : 0;
  const frame = moving ? Math.floor(now / FRAME_MS) % 4 : 0;
  const img = p.dir === "up" ? sprites.pup_up : p.dir === "down" ? sprites.pup_down : sprites.pup_side;
  if (PUP_HOP) {
    ctx.fillStyle = `rgba(0,0,0,${0.3 - hopK * 0.05})`;
    ctx.beginPath();
    ctx.ellipse(cx, cy + cell * 0.42, cell * (0.34 - hopK * 0.03), cell * (0.12 - hopK * 0.01), 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.translate(cx, cy + walk);
    ctx.scale(1 - hopK * 0.025, 1 + hopK * 0.035);
    ctx.translate(-cx, -(cy + walk));
  }
  drawSprite(ctx, img, cx, cy + walk, cell * 1.18, p.dir === "left", frame, () => {
    ctx.fillStyle = "#f5f5f4";
    ctx.beginPath();
    ctx.arc(0, 0, cell * 0.36, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.restore();
}

function drawPickups(ctx: CanvasRenderingContext2D, sprites: Sprites, s: GameState, cell: number, now: number) {
  for (const pu of s.powerups) {
    if (pu.ttl < 3000 && Math.floor(now / 150) % 2 === 0) continue;
    const bob = Math.sin(now / 250 + pu.x) * cell * 0.06;
    const cx = (pu.x + 0.5) * cell, cy = (pu.y + 0.5) * cell + bob;
    const img = pu.kind === "freeze" ? sprites.power_freeze : sprites.power_ghost;
    ctx.save();
    ctx.shadowColor = pu.kind === "freeze" ? "#7dd3fc" : "#fde68a";
    ctx.shadowBlur = 14 + Math.sin(now / 200) * 4;
    if (img) ctx.drawImage(img, cx - cell * 0.38, cy - cell * 0.38, cell * 0.76, cell * 0.76);
    else { ctx.fillStyle = pu.kind === "freeze" ? "#7dd3fc" : "#fde68a"; ctx.beginPath(); ctx.arc(cx, cy, cell * 0.3, 0, Math.PI * 2); ctx.fill(); }
    ctx.restore();
  }
  for (const b of s.bubbles) {
    if (b.ttl < 3000 && Math.floor(now / 150) % 2 === 0) continue;
    const bob = Math.sin(now / 300 + b.idx) * cell * 0.08;
    const cx = (b.x + 0.5) * cell, cy = (b.y + 0.5) * cell + bob;
    const r = cell * 0.38;
    ctx.save();
    const g = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, r * 0.1, cx, cy, r);
    g.addColorStop(0, "rgba(255,255,255,0.85)");
    g.addColorStop(0.6, "rgba(255,255,255,0.15)");
    g.addColorStop(1, "rgba(255,255,255,0.45)");
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.9)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.font = `700 ${cell * 0.5}px Fredoka, Outfit, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = LETTER_COLORS[b.idx];
    ctx.strokeStyle = "rgba(0,0,0,0.5)";
    ctx.lineWidth = cell * 0.06;
    ctx.strokeText(LETTERS[b.idx], cx, cy + 1);
    ctx.fillText(LETTERS[b.idx], cx, cy + 1);
    ctx.restore();
  }
}

function drawBars(ctx: CanvasRenderingContext2D, s: GameState) {
  const bars = [
    { v: s.power / 9000, color: "#4ade80", label: "POWER" },
    { v: s.freeze / 6000, color: "#7dd3fc", label: "FREEZE" },
    { v: s.ghost / 6000, color: "#fde68a", label: "GHOST" },
  ].filter((b) => b.v > 0);
  bars.forEach((b, i) => {
    const w = 110, h = 8, x = 10, y = 10 + i * 20;
    ctx.fillStyle = "rgba(11,12,16,0.7)";
    roundRect(ctx, x - 4, y - 4, w + 62, h + 8, 6);
    ctx.fill();
    ctx.fillStyle = b.color;
    roundRect(ctx, x, y, Math.max(2, w * Math.min(1, b.v)), h, 4);
    ctx.fill();
    ctx.font = "bold 9px 'JetBrains Mono', monospace";
    ctx.fillStyle = "#fff";
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    ctx.fillText(b.label, x + w + 8, y + h);
  });
}

const TUNNEL_VIEW = 11;

export function render(ctx: CanvasRenderingContext2D, s: GameState, sprites: Sprites, width: number, height: number, now: number) {
  const cell = s.bonus ? width / TUNNEL_VIEW : width / s.cols;
  ctx.clearRect(0, 0, width, height);
  ctx.save();
  if (s.shake > 0) ctx.translate((Math.random() - 0.5) * s.shake, (Math.random() - 0.5) * s.shake);

  let camX = 0, camY = 0;
  if (s.bonus) {
    const pp = lerpPos(s.player);
    const viewRows = height / cell;
    camX = Math.min(Math.max(pp.x + 0.5 - TUNNEL_VIEW / 2, 0), s.cols - TUNNEL_VIEW);
    camY = Math.min(Math.max(pp.y + 0.5 - viewRows / 2, 0), s.rows - viewRows);
    ctx.translate(-camX * cell, -camY * cell);
  }
  const gridW = s.cols * cell, gridH = s.rows * cell;

  const theme = themeFor(s.level);
  const bg = sprites[`bg_${theme}` as const];
  if (bg && !s.bonus) {
    ctx.drawImage(bg, 0, 0, width, height);
  } else if (bg) {
    for (let ty = 0; ty < gridH; ty += gridW) ctx.drawImage(bg, 0, ty, gridW, gridW);
  } else {
    ctx.fillStyle = "#7c2d12";
    ctx.fillRect(0, 0, gridW, gridH);
  }
  ctx.strokeStyle = "rgba(255,255,255,0.10)";
  ctx.lineWidth = 1;
  for (let i = 1; i < s.cols; i++) { ctx.beginPath(); ctx.moveTo(i * cell, 0); ctx.lineTo(i * cell, gridH); ctx.stroke(); }
  for (let i = 1; i < s.rows; i++) { ctx.beginPath(); ctx.moveTo(0, i * cell); ctx.lineTo(gridW, i * cell); ctx.stroke(); }

  if (s.bonus) drawTunnelWalls(ctx, s, cell, now);

  for (let y = 0; y < s.rows; y++) for (let x = 0; x < s.cols; x++) {
    if (s.grid[y][x] !== Cell.Crystal) continue;
    const f = s.fading.find((q) => q.x === x && q.y === y);
    const alpha = f ? Math.max(0.25, 1 - (f.t / 8000) * 0.8) : 1;
    drawCrystal(ctx, s, sprites, s.kinds[y][x] ?? "ball", x * cell, y * cell, cell, now, alpha);
  }

  for (const sl of s.sliders) {
    const p = lerpPos(sl as Mover);
    const back = { x: p.x - (sl.tx - sl.x) * 0.5, y: p.y - (sl.ty - sl.y) * 0.5 };
    drawCrystal(ctx, s, sprites, sl.kind, back.x * cell, back.y * cell, cell, now, 0.3);
    drawCrystal(ctx, s, sprites, sl.kind, p.x * cell, p.y * cell, cell, now);
  }

  drawPickups(ctx, sprites, s, cell, now);
  for (const sp of s.spawns) {
    const k = Math.min(1, Math.max(0, 1 - sp.timer / sp.max));
    const pulse = 0.5 + 0.5 * Math.sin(now / 70);
    ctx.save();
    ctx.globalAlpha = 0.35 + k * 0.5;
    ctx.strokeStyle = "#fb7185";
    ctx.lineWidth = Math.max(2, cell * 0.08);
    ctx.setLineDash([cell * 0.15, cell * 0.1]);
    ctx.lineDashOffset = -now / 20;
    ctx.beginPath();
    ctx.arc((sp.x + 0.5) * cell, (sp.y + 0.5) * cell, cell * (0.28 + pulse * 0.1), 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = `rgba(251,113,133,${0.15 + k * 0.35})`;
    ctx.beginPath();
    ctx.arc((sp.x + 0.5) * cell, (sp.y + 0.5) * cell, cell * 0.3 * k, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  const edible = s.power > 0;
  for (const e of s.enemies) if (!(e.kind === "bat" && e.flying)) drawEnemy(ctx, sprites, e, cell, now, s.freeze > 0 || e.frozen > 0, edible);
  drawPlayer(ctx, sprites, s, cell, now);
  for (const e of s.enemies) if (e.kind === "bat" && e.flying) drawEnemy(ctx, sprites, e, cell, now, s.freeze > 0 || e.frozen > 0, edible);

  for (const p of s.particles) {
    ctx.globalAlpha = Math.max(0, p.life / p.max);
    ctx.fillStyle = p.color;
    if (p.star) {
      const r = p.size * cell * 0.9, px = p.x * cell, py = p.y * cell;
      ctx.beginPath();
      ctx.moveTo(px, py - r); ctx.lineTo(px + r * 0.3, py - r * 0.3); ctx.lineTo(px + r, py); ctx.lineTo(px + r * 0.3, py + r * 0.3);
      ctx.lineTo(px, py + r); ctx.lineTo(px - r * 0.3, py + r * 0.3); ctx.lineTo(px - r, py); ctx.lineTo(px - r * 0.3, py - r * 0.3);
      ctx.closePath(); ctx.fill();
      continue;
    }
    ctx.fillRect((p.x - p.size / 2) * cell, (p.y - p.size / 2) * cell, p.size * cell, p.size * cell);
  }
  ctx.globalAlpha = 1;

  for (const f of s.flashes) {
    const k = Math.min(1, Math.max(0, 1 - f.life / f.max));
    ctx.save();
    ctx.globalAlpha = (1 - k) * 0.9;
    ctx.strokeStyle = f.color;
    ctx.lineWidth = Math.max(2, cell * 0.12 * (1 - k));
    ctx.shadowColor = f.color;
    ctx.shadowBlur = 20;
    ctx.beginPath();
    ctx.arc(f.x * cell, f.y * cell, cell * f.radius * (0.2 + k * 0.8), 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = Math.max(0, 0.7 - k * 1.4);
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(f.x * cell, f.y * cell, cell * 0.5 * (1 - k), 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  for (const pop of s.popups) {
    const k = 1 - pop.life / pop.max;
    const scale = 0.6 + Math.min(1, k * 4) * 0.6 + k * 0.3;
    const big = pop.text.startsWith("x");
    if (pop.text === "POWER!" && sprites.bonus_burst) {
      const img = sprites.bonus_burst;
      const w = cell * 3.6 * scale, h = w * (img.height / img.width);
      ctx.save();
      ctx.globalAlpha = k < 0.7 ? 1 : 1 - (k - 0.7) / 0.3;
      const cxp = Math.min(Math.max(pop.x * cell, w / 2), gridW - w / 2);
      const cyp = Math.min(Math.max(pop.y * cell - k * cell, h / 2), gridH - h / 2);
      ctx.drawImage(img, cxp - w / 2, cyp - h / 2, w, h);
      ctx.restore();
      continue;
    }
    ctx.save();
    ctx.globalAlpha = k < 0.7 ? 1 : 1 - (k - 0.7) / 0.3;
    ctx.translate(Math.min(Math.max(pop.x * cell, cell * 1.5), gridW - cell * 1.5), pop.y * cell - k * cell * 1.2);
    ctx.scale(scale, scale);
    ctx.font = `700 ${cell * (big ? 1.1 : 0.7)}px Fredoka, Outfit, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineWidth = cell * 0.14;
    ctx.strokeStyle = "#1e3a8a";
    ctx.strokeText(pop.text, 0, 0);
    ctx.shadowColor = "#fff";
    ctx.shadowBlur = 12;
    ctx.fillStyle = pop.text === "POWER!" ? "#86efac" : pop.text === "PUPPY!" ? "#f9a8d4" : pop.text.startsWith("BUM") ? "#fb923c" : "#fde047";
    ctx.fillText(pop.text, 0, 0);
    if (big) {
      ctx.shadowBlur = 0;
      ctx.font = `600 ${cell * 0.4}px Fredoka, Outfit, sans-serif`;
      ctx.fillStyle = "#fff";
      ctx.fillText(t("combo"), 0, cell * 0.75);
    }
    ctx.restore();
  }

  if (s.freeze > 0) {
    ctx.fillStyle = `rgba(125,211,252,${0.08 + Math.min(0.1, s.freeze / 60000)})`;
    ctx.fillRect(0, 0, gridW, gridH);
  }
  ctx.restore();
  if (s.bonus) {
    const vg = ctx.createRadialGradient(width / 2, height / 2, width * 0.3, width / 2, height / 2, width * 0.75);
    vg.addColorStop(0, "rgba(0,0,0,0)");
    vg.addColorStop(1, "rgba(0,0,0,0.6)");
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, width, height);
    drawTimer(ctx, s, width, now);
  }
  drawBars(ctx, s);
}

function drawTimer(ctx: CanvasRenderingContext2D, s: GameState, width: number, now: number) {
  const secs = Math.ceil(s.timeLeft / 1000);
  const low = secs <= 10;
  const pulse = low ? 1 + 0.12 * Math.abs(Math.sin(now / 160)) : 1;
  const size = Math.max(22, width * 0.085) * pulse;
  const x = width - 14, y = 14;
  ctx.save();
  ctx.font = `700 ${size}px Fredoka, Outfit, sans-serif`;
  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  const label = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
  ctx.lineWidth = size * 0.18;
  ctx.strokeStyle = "rgba(0,0,0,0.75)";
  ctx.strokeText(label, x, y);
  ctx.shadowColor = low ? "#f87171" : "#fde047";
  ctx.shadowBlur = low ? 18 : 8;
  ctx.fillStyle = low ? "#fca5a5" : "#fef3c7";
  ctx.fillText(label, x, y);
  ctx.shadowBlur = 0;
  const barW = width * 0.3, barH = 6;
  ctx.fillStyle = "rgba(0,0,0,0.5)";
  ctx.fillRect(x - barW, y + size + 6, barW, barH);
  ctx.fillStyle = low ? "#ef4444" : "#facc15";
  ctx.fillRect(x - barW, y + size + 6, barW * (s.timeLeft / s.timeMax), barH);
  ctx.restore();
}

function drawTunnelWalls(ctx: CanvasRenderingContext2D, s: GameState, cell: number, now: number) {
  ctx.save();
  for (let y = 0; y < s.rows; y++) for (let x = 0; x < s.cols; x++) {
    if (s.grid[y][x] !== Cell.Wall) continue;
    const g = ctx.createLinearGradient(x * cell, y * cell, x * cell, (y + 1) * cell);
    g.addColorStop(0, "#1c0a03");
    g.addColorStop(1, "#0c0402");
    ctx.fillStyle = g;
    ctx.fillRect(x * cell, y * cell, cell + 0.5, cell + 0.5);
    ctx.fillStyle = "rgba(255,255,255,0.05)";
    ctx.fillRect(x * cell + cell * 0.15, y * cell + cell * 0.2, cell * 0.25, cell * 0.12);
    const edgeFloor = (dx: number, dy: number) => inBounds(x + dx, y + dy, s.cols, s.rows) && s.grid[y + dy][x + dx] !== Cell.Wall;
    ctx.fillStyle = "rgba(251,191,36,0.35)";
    if (edgeFloor(0, 1)) ctx.fillRect(x * cell, (y + 1) * cell - cell * 0.08, cell, cell * 0.08);
    if (edgeFloor(0, -1)) ctx.fillRect(x * cell, y * cell, cell, cell * 0.08);
    if (edgeFloor(1, 0)) ctx.fillRect((x + 1) * cell - cell * 0.08, y * cell, cell * 0.08, cell);
    if (edgeFloor(-1, 0)) ctx.fillRect(x * cell, y * cell, cell * 0.08, cell);
  }
  if (s.exit) {
    const ex = (s.exit.x + 1) * cell, ey = (s.exit.y + 0.5) * cell;
    const pulse = 0.5 + 0.5 * Math.sin(now / 250);
    const glow = ctx.createRadialGradient(ex, ey, cell * 0.1, ex, ey, cell * (1.4 + pulse * 0.3));
    glow.addColorStop(0, "rgba(255,251,235,0.95)");
    glow.addColorStop(0.35, "rgba(253,224,71,0.55)");
    glow.addColorStop(1, "rgba(253,224,71,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(ex - cell * 2, ey - cell * 2, cell * 4, cell * 4);
    ctx.font = `700 ${cell * 0.32}px Fredoka, Outfit, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#7c2d12";
    ctx.fillText(t("exit"), ex, ey + cell * 0.55);
  }
  ctx.restore();
}
